import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { formatPrice, getSymbol } from '../utils/currencyUtils';
import {
    FaMapMarkerAlt,
    FaClock,
    FaStar,
    FaUsers,
    FaCalendarAlt,
    FaCheck,
    FaTimes,
    FaPlay,
    FaHeart,
    FaRegHeart,
    FaShareAlt,
    FaInfoCircle,
    FaShieldAlt,
    FaExclamationTriangle,
    FaGlobe,
    FaChild,
    FaUserFriends,
    FaChevronLeft,
    FaChevronRight,
    FaBolt,
    FaDownload
} from 'react-icons/fa';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { calculateProcessingFees } from '../services/util/processingFeesService';
import cartService from '../services/cartService';
import { toast } from 'react-hot-toast';
import MainLayout from '../components/Layout/MainLayout';
import { useCart } from '../contexts/CartContext';
import { ExperiencePdfTemplate } from '../components/ExperiencePdfTemplate';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

/** Strip emoji and pictographic characters from CMS or user-authored copy */
function stripEmojis(str) {
    if (str == null) return str;
    if (typeof str !== 'string') return str;
    return str
        .replace(/\p{Extended_Pictographic}/gu, '')
        .replace(/[\uFE0F\u200D]/g, '')
        .replace(/\s{2,}/g, ' ')
        .trim();
}

// const getProxiedUrl = (url) => {
//     if (!url) return '';
//     if (url.startsWith('http')) {
//         const backendOrigin = API_BASE_URL.replace('/api', '');
//         return `${backendOrigin}/api/proxy?url=${encodeURIComponent(url)}`;
//     }
//     return url;
// };

// const extractVideoKeyframes = (videoUrl, count = 3) => {
//     return new Promise((resolve) => {
//         const video = document.createElement('video');
//         video.crossOrigin = 'anonymous';
//         video.src = videoUrl;
//         video.muted = true;
//         video.playsInline = true;
        
//         video.addEventListener('loadedmetadata', async () => {
//             const duration = video.duration;
//             if (isNaN(duration) || duration <= 0) {
//                 resolve([]);
//                 return;
//             }
            
//             const keyframes = [];
//             const canvas = document.createElement('canvas');
//             const ctx = canvas.getContext('2d');
            
//             canvas.width = 640;
//             canvas.height = 360;
            
//             const times = [];
//             for (let i = 1; i <= count; i++) {
//                 times.push((duration / (count + 1)) * i);
//             }
            
//             for (const time of times) {
//                 try {
//                     await new Promise((seekResolve, seekReject) => {
//                         const onSeeked = () => {
//                             video.removeEventListener('seeked', onSeeked);
//                             video.removeEventListener('error', onError);
//                             seekResolve();
//                         };
//                         const onError = (e) => {
//                             video.removeEventListener('seeked', onSeeked);
//                             video.removeEventListener('error', onError);
//                             seekReject(e);
//                         };
//                         video.addEventListener('seeked', onSeeked);
//                         video.addEventListener('error', onError);
//                         video.currentTime = time;
//                     });
                    
//                     ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
//                     const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
//                     keyframes.push(dataUrl);
//                 } catch (err) {
//                     console.error('Error seeking video at time', time, err);
//                 }
//             }
            
//             resolve(keyframes);
//         });
        
//         video.addEventListener('error', () => {
//             console.error('Error loading video for keyframes');
//             resolve([]);
//         });
        
//         video.load();
//     });
// };

/**
 * Fetches a URL (typically through the /api/proxy endpoint) and returns it as
 * a base64 data URL so html2canvas never has to make a network request for
 * images — eliminating CORS / Content-Encoding issues across all environments.
 */
const fetchImageAsBase64 = async (url) => {
    if (!url) return null;
    try {
        const response = await fetch(url, { cache: 'no-cache' });
        if (!response.ok && response.status !== 304) return null;
        const blob = await response.blob();
        if (!blob || blob.size === 0) return null;

        // Ensure valid image MIME type if response returned application/octet-stream or generic type
        let mimeType = blob.type;
        if (!mimeType || mimeType === 'application/octet-stream' || mimeType === 'binary/octet-stream' || mimeType === 'text/plain') {
            const lowerUrl = url.toLowerCase();
            if (lowerUrl.includes('.png')) mimeType = 'image/png';
            else if (lowerUrl.includes('.webp')) mimeType = 'image/webp';
            else if (lowerUrl.includes('.gif')) mimeType = 'image/gif';
            else mimeType = 'image/jpeg';
        }

        const correctedBlob = new Blob([blob], { type: mimeType });
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result ?? null);
            reader.onerror  = () => resolve(null);
            reader.readAsDataURL(correctedBlob);
        });
    } catch {
        return null;
    }
};

const ExperienceDetailsPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { refreshCartCount } = useCart();
    const [experience, setExperience] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [isFavorite, setIsFavorite] = useState(false);
    const [participants, setParticipants] = useState(1);
    const [isInlinePlaying, setIsInlinePlaying] = useState(false);
    const [showVideoModal, setShowVideoModal] = useState(false);

    // Availability calendar states
    const [availableDates, setAvailableDates] = useState([]);
    const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
    const [selectedDate, setSelectedDate] = useState('');
    const [selectedTimeSlot, setSelectedTimeSlot] = useState('');
    const dateStripRef = useRef(null);

    // Gallery states
    const [showFullGallery, setShowFullGallery] = useState(false);
    const [selectedGalleryIndex, setSelectedGalleryIndex] = useState(0);

    // Processing Fee state
    const [processingFeeData, setProcessingFeeData] = useState(null);
    const [isCalculatingFees, setIsCalculatingFees] = useState(false);

    // Add to cart state
    const [isAddingToCart, setIsAddingToCart] = useState(false);
    
    // PDF download state
    const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);

    useEffect(() => {
        fetchExperience();
    }, [id]);

    useEffect(() => {
        const updateFees = async () => {
            if (experience && participants) {
                setIsCalculatingFees(true);
                try {
                    const priceType = experience.pricing?.priceType || 'per_person';
                    const basePrice = experience.basePrice || experience.pricing?.basePrice || 0;
                    const commissionAmount = experience.commissionAmount || 0;
                    const pricePerUnit = basePrice + commissionAmount;

                    const subtotal = priceType === 'per_group' ? pricePerUnit : pricePerUnit * participants;
                    const currency = experience.pricing?.currency;
                    if (!currency) {
                        console.warn('Experience is missing a pricing currency; fee calculation skipped.');
                        return;
                    }

                    const result = await calculateProcessingFees(subtotal, currency);
                    setProcessingFeeData(result);
                } catch (err) {
                    console.error('Error updating fees:', err);
                } finally {
                    setIsCalculatingFees(false);
                }
            }
        };

        updateFees();
    }, [experience, participants]);

    // Keyboard navigation for gallery
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (showFullGallery) {
                if (e.key === 'ArrowRight') {
                    handleNextImage();
                } else if (e.key === 'ArrowLeft') {
                    handlePrevImage();
                } else if (e.key === 'Escape') {
                    setShowFullGallery(false);
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [showFullGallery, selectedGalleryIndex, experience]); // Add experience to dependencies

    const handleNextImage = () => {
        if (experience && experience.images) {
            setSelectedGalleryIndex((prevIndex) =>
                (prevIndex + 1) % experience.images.length
            );
        }
    };

    const handlePrevImage = () => {
        if (experience && experience.images) {
            setSelectedGalleryIndex((prevIndex) =>
                (prevIndex - 1 + experience.images.length) % experience.images.length
            );
        }
    };

    const scrollDateStrip = (direction) => {
        if (dateStripRef.current) {
            const scrollAmount = direction === 'left' ? -300 : 300;
            dateStripRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
        }
    };

    const scrollToMonth = (monthYear) => {
        if (dateStripRef.current) {
            const targetDate = availableDates.find(d => {
                const date = new Date(d.date);
                const my = `${date.toLocaleString('default', { month: 'long' })} ${date.getFullYear()}`;
                return my === monthYear;
            });

            if (targetDate) {
                const index = availableDates.indexOf(targetDate);
                const buttons = dateStripRef.current.querySelectorAll('button');
                if (buttons[index]) {
                    buttons[index].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
                }
            }
        }
    };

    const getAvailableMonths = () => {
        const months = new Set();
        availableDates.forEach(d => {
            const date = new Date(d.date);
            months.add(`${date.toLocaleString('default', { month: 'long' })} ${date.getFullYear()}`);
        });
        return Array.from(months);
    };

    const fetchExperience = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_BASE_URL}/experiences/${id}`);
            const data = response.data.data || response.data;

            setExperience(data);

            // Process availability data
            if (data.availability) {
                const processedDates = processAvailableDates(data.availability, data.leadTime || 0);
                setAvailableDates(processedDates);

                // Set time slots from timeRanges
                const timeSlots = data.availability.timeRanges || [];
                setAvailableTimeSlots(timeSlots);
            }

            setError('');
        } catch (err) {
            console.error('Error fetching experience:', err);
            setError('Failed to load experience details');
        } finally {
            setLoading(false);
        }
    };

    const formatDuration = (duration) => {
        if (!duration) return 'Flexible Duration';
        const parts = [];
        if (duration.days) parts.push(`${duration.days} Day${duration.days > 1 ? 's' : ''}`);
        if (duration.hours) parts.push(`${duration.hours}h`);
        if (duration.minutes) parts.push(`${duration.minutes}m`);
        return parts.join(' ') || 'Flexible Duration';
    };

    const formatPriceDisplay = (pricing) => {
        if (!pricing?.basePrice) return 'Price on Request';

        // Use detailTotal if available (includes commission), otherwise fall back to basePrice
        const displayPrice = experience?.detailTotal || experience?.listingTotal || pricing.basePrice;
        return formatPrice(displayPrice, pricing.currency);
    };

    const calculateTotal = () => {
        if (processingFeeData) return processingFeeData.totalWithFees;

        if (!experience?.pricing?.basePrice) return 0;

        const basePrice = experience.basePrice || experience.pricing.basePrice || 0;
        const commissionAmount = experience.commissionAmount || 0;
        const pricePerUnit = basePrice + commissionAmount;
        const priceType = experience.pricing?.priceType || 'per_person';

        const subtotal = priceType === 'per_group' ? pricePerUnit : pricePerUnit * participants;
        return subtotal; // This is a fallback if fee data isn't loaded yet
    };

    const getCoverImage = () => {
        if (experience?.images?.length > 0) {
            const coverImage = experience.images.find(img => img.isCover) || experience.images[0];
            return coverImage.url || coverImage;
        }
        return 'https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?q=80&w=2021&auto=format&fit=crop';
    };

    const getCoverVideo = () => experience?.videos?.coverVideo?.url || null;

    const handleAddToCart = async () => {
        // Validate selections
        if (!selectedDate) {
            toast.error('Please select a date');
            const el = document.getElementById('availability-section');
            el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }

        if (availableTimeSlots.length > 0 && !selectedTimeSlot) {
            toast.error('Please select a time slot');
            return;
        }

        setIsAddingToCart(true);

        try {
            const newCurrency = experience.pricing?.currency;
            if (!newCurrency) {
                toast.error('This experience is missing a currency. Please contact support.');
                setIsAddingToCart(false);
                return;
            }

            const priceType = experience.pricing?.priceType || 'per_person';
            const basePrice = experience.basePrice || experience.pricing?.basePrice || 0;
            const commissionAmount = experience.commissionAmount || 0;
            const pricePerUnit = basePrice + commissionAmount;
            const subtotal = priceType === 'per_group' ? pricePerUnit : pricePerUnit * participants;

            const cartItem = {
                type: 'experience',
                experienceId: experience._id || id,
                title: experience.title,
                image: getCoverImage(),
                category: experience.category,
                location: experience.location,
                duration: experience.duration,
                difficulty: experience.difficulty,
                transformationArc: experience.transformationArc,
                selectedDate: selectedDate,
                selectedTimeSlot: selectedTimeSlot,
                participantCount: participants,
                basePrice: basePrice,
                pricePerPerson: pricePerUnit,
                price: subtotal,
                currency: experience.pricing?.currency,
                commissionAmount: commissionAmount,
                highlights: experience.highlights || [],
                included: experience.included || [],
                notIncluded: experience.notIncluded || [],
                languages: experience.languages || [],
                maxParticipants: experience.pricing?.maxParticipants || experience.capacity?.maxParticipants,
                minParticipants: experience.pricing?.minParticipants || experience.capacity?.minParticipants,
                rating: experience.rating,
                reviewCount: experience.reviewCount,
                cancellationPolicy: experience.cancellationPolicy,
                totalWithProcessingFees: processingFeeData?.totalWithFees || subtotal,
                priceDetails: {
                    baseRate: subtotal,
                    tax: processingFeeData?.totalProcessingFees || 0,
                    additionalFees: 0,
                    processingFees: processingFeeData,
                    totalWithFees: processingFeeData?.totalWithFees || subtotal
                },
                pricing: {
                    basePrice: basePrice,
                    currency: experience.pricing?.currency,
                    priceType: priceType,
                    processingFees: processingFeeData,
                    totalWithProcessingFees: processingFeeData?.totalWithFees || subtotal
                }
            };

            await cartService.addToCart(cartItem);
            refreshCartCount();
            toast.success('Experience added to cart!');

            // Optionally navigate to cart or show a success message
            // navigate('/cart');

        } catch (error) {
            console.error('Error adding to cart:', error);
            toast.error(error.message || 'Failed to add to cart');
        } finally {
            setIsAddingToCart(false);
        }
    };

    const handleDownloadPdf = async () => {
        try {
            setIsDownloadingPdf(true);
            const { default: html2pdf } = await import('html2pdf.js');
            const element = document.getElementById('experience-pdf-template');

            // ── Step 1: convert every <img> src to a base64 data URL ──────────
            // Doing this before html2pdf runs means html2canvas never has to
            // fetch anything over the network, so no CORS / proxy / encoding
            // issues can affect image rendering in the PDF.
            const imgEls  = Array.from(element.querySelectorAll('img'));
            const savedSrcs = imgEls.map(el => el.src);

            await Promise.all(imgEls.map(async (imgEl) => {
                const b64 = await fetchImageAsBase64(imgEl.src);
                if (b64) {
                    imgEl.src = b64;
                    if (imgEl.decode) {
                        await imgEl.decode().catch(() => {});
                    }
                }
            }));

            // ── Step 2: convert the cover CSS background-image to base64 ─────
            const coverEl = element.querySelector('.pdf-cover-page');
            let savedBg = null;
            if (coverEl) {
                savedBg = coverEl.style.backgroundImage;
                const match = savedBg.match(/url\(['"]?([^'"()]+)['"]?\)/);
                if (match?.[1]) {
                    const b64 = await fetchImageAsBase64(match[1]);
                    if (b64) coverEl.style.backgroundImage = `url("${b64}")`;
                }
            }

            // Small delay to allow the browser to complete DOM layout & paint before html2pdf captures canvas
            await new Promise((resolve) => setTimeout(resolve, 150));

            // ── Step 3: generate the PDF (all images are now inline base64) ──
            await html2pdf().from(element).set({
                margin:      0,
                filename:    `${experience?.title || 'Experience'}_Details.pdf`,
                image:       { type: 'jpeg', quality: 0.98 },
                html2canvas: {
                    scale:       2,
                    useCORS:     false,   // not needed — images are already base64
                    allowTaint:  false,
                    logging:     false,
                    imageTimeout: 0,      // 0 = no timeout; base64 images load instantly
                    width:       794,
                    scrollX:     0,
                    scrollY:     0,
                },
                jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
            }).save();

            toast.success('PDF downloaded successfully!');

            // ── Step 4: restore original src / background values ─────────────
            imgEls.forEach((el, i) => { el.src = savedSrcs[i]; });
            if (coverEl && savedBg !== null) coverEl.style.backgroundImage = savedBg;

        } catch (err) {
            console.error('Error generating PDF:', err);
            toast.error('Failed to generate PDF.');
        } finally {
            setIsDownloadingPdf(false);
        }
    };


    // Comprehensive itinerary detection and conversion
    const processItinerary = () => {
        const itin = experience?.itinerary;

        if (!itin) {
            console.log('No itinerary data');
            return { hasItinerary: false, isHTML: false, content: null, structured: null };
        }

        console.log('Raw itinerary:', itin);
        console.log('Itinerary type:', typeof itin);
        console.log('Is array:', Array.isArray(itin));

        // Case 1: Direct string (HTML)
        if (typeof itin === 'string' && itin.trim().length > 0) {
            console.log('Detected: Direct HTML string');
            return { hasItinerary: true, isHTML: true, content: itin, structured: null };
        }

        // Case 2: Array
        if (Array.isArray(itin) && itin.length > 0) {
            const firstItem = itin[0];
            console.log('First array item type:', typeof firstItem);
            console.log('First array item:', firstItem);

            // Case 2a: Array of strings (join to HTML)
            if (typeof firstItem === 'string') {
                const htmlContent = itin.join('');
                console.log('Detected: Array of character strings');
                return { hasItinerary: true, isHTML: true, content: htmlContent, structured: null };
            }

            // Case 2b: Array of structured objects (with step/title)
            if (typeof firstItem === 'object' && firstItem !== null &&
                (firstItem.hasOwnProperty('step') || firstItem.hasOwnProperty('title'))) {
                console.log('Detected: Structured itinerary array');
                return { hasItinerary: true, isHTML: false, content: null, structured: itin };
            }

            // Case 2c: Array with object containing numeric keys (character-indexed)
            if (typeof firstItem === 'object' && firstItem !== null) {
                const keys = Object.keys(firstItem);
                console.log('Object keys sample:', keys.slice(0, 10));
                console.log('Keys length:', keys.length);
                console.log('Testing numeric check on first 5 keys:');
                keys.slice(0, 5).forEach(key => {
                    console.log(`  Key "${key}": isNaN(parseInt("${key}")) =`, isNaN(parseInt(key)));
                });

                const allKeysNumeric = keys.every(key => !isNaN(parseInt(key)));
                console.log('All keys numeric?', allKeysNumeric);

                if (keys.length > 0 && allKeysNumeric) {
                    // Reconstruct HTML from character-indexed object
                    const htmlContent = Object.values(firstItem).join('');
                    console.log('Detected: Character-indexed object, reconstructed HTML length:', htmlContent.length);
                    console.log('HTML preview:', htmlContent.substring(0, 100));
                    return { hasItinerary: true, isHTML: true, content: htmlContent, structured: null };
                }
                console.log('Falling through Case 2c - condition not met');
            }
        }

        // Case 3: Direct object (not array) with numeric keys
        if (typeof itin === 'object' && !Array.isArray(itin) && itin !== null) {
            const keys = Object.keys(itin);
            console.log('Direct object keys sample:', keys.slice(0, 10));

            if (keys.length > 0 && keys.every(key => !isNaN(parseInt(key)))) {
                const htmlContent = Object.values(itin).join('');
                console.log('Detected: Direct character-indexed object, reconstructed HTML length:', htmlContent.length);
                return { hasItinerary: true, isHTML: true, content: htmlContent, structured: null };
            }
        }

        console.log('No valid itinerary format detected');
        return { hasItinerary: false, isHTML: false, content: null, structured: null };
    };

    // Process available dates from availability data with lead time blocking
    const processAvailableDates = (availabilityData, leadTime = 0) => {
        if (!availabilityData) return [];

        const availableDates = [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // Calculate the first allowed booking date based on lead time
        const minAllowedDate = new Date(today);
        minAllowedDate.setDate(minAllowedDate.getDate() + leadTime);
        const minAllowedDateStr = minAllowedDate.toISOString().split('T')[0];

        const endDate = new Date(today);
        endDate.setMonth(endDate.getMonth() + 6); // Look ahead 6 months

        // Get blackout dates
        const blackoutDates = (availabilityData.blackoutDates || []).map(bd =>
            new Date(bd.date).toISOString().split('T')[0]
        );

        // Process date ranges if available
        if (availabilityData.dateRanges && availabilityData.dateRanges.length > 0) {
            availabilityData.dateRanges.forEach((range) => {
                if (!range.isActive) return;

                const rangeStartDate = new Date(range.startDate);
                const rangeEndDate = new Date(range.endDate);

                if (rangeEndDate >= today) {
                    const actualStartDate = rangeStartDate >= today ? rangeStartDate : today;
                    const actualEndDate = rangeEndDate <= endDate ? rangeEndDate : endDate;

                    for (let date = new Date(actualStartDate); date <= actualEndDate; date.setDate(date.getDate() + 1)) {
                        const dateStr = date.toISOString().split('T')[0];

                        if (dateStr >= minAllowedDateStr) {
                            if (!blackoutDates.includes(dateStr)) {
                                if (!availabilityData.daysOfWeek || availabilityData.daysOfWeek.length === 0 ||
                                    isDayOfWeekAvailable(date, availabilityData.daysOfWeek)) {
                                    availableDates.push({
                                        date: dateStr,
                                        label: range.label || null
                                    });
                                }
                            }
                        }
                    }
                }
            });
        } else {
            // Use days of week pattern or default to all days
            const daysOfWeek = availabilityData.daysOfWeek || [];

            for (let date = new Date(today); date <= endDate; date.setDate(date.getDate() + 1)) {
                const dateStr = date.toISOString().split('T')[0];

                if (dateStr >= minAllowedDateStr) {
                    if (!blackoutDates.includes(dateStr)) {
                        if (daysOfWeek.length === 0 || isDayOfWeekAvailable(date, daysOfWeek)) {
                            availableDates.push({
                                date: dateStr,
                                label: null
                            });
                        }
                    }
                }
            }
        }
        return availableDates;
    };

    // Check if a date falls on an available day of week
    const isDayOfWeekAvailable = (date, daysOfWeek) => {
        const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        const dayName = dayNames[date.getDay()];
        return daysOfWeek.includes(dayName);
    };

    // Handle date selection
    const handleDateChange = (dateValue) => {
        setSelectedDate(dateValue);
        setSelectedTimeSlot(''); // Reset time slot when date changes

        // Find the button and scroll it into view
        setTimeout(() => {
            const selectedButton = dateStripRef.current?.querySelector('.bg-black');
            if (selectedButton) {
                selectedButton.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
            }
        }, 100);
    };

    // Dynamically render category-specific content
    const renderCategoryContent = () => {
        const spec = experience?.categorySpecific || {};

        // Check if categorySpecific is a string (JSON) and parse it
        let categoryData = spec;
        if (typeof spec === 'string') {
            try {
                categoryData = JSON.parse(spec);
            } catch (e) {
                console.error('Error parsing categorySpecific:', e);
                categoryData = {};
            }
        }

        // If no category-specific data, return null
        if (!categoryData || Object.keys(categoryData).length === 0) {
            return null;
        }

        // Format label from camelCase
        const formatLabel = (key) => {
            return stripEmojis(
                key
                    .replace(/([A-Z])/g, ' $1')
                    .replace(/^./, str => str.toUpperCase())
                    .trim()
            );
        };

        const formatValue = (value) => {
            if (Array.isArray(value)) {
                if (value.length === 0) return 'N/A';
                return stripEmojis(value.map((v) => String(v)).join(', '));
            }
            if (typeof value === 'boolean') {
                return value ? 'Yes' : 'No';
            }
            const raw = value ?? 'N/A';
            return typeof raw === 'string' ? stripEmojis(raw) : raw;
        };

        const entries = Object.entries(categoryData)
            .filter(([key, value]) => value !== null && value !== undefined && value !== '')
            .map(([key, value]) => ({
                label: formatLabel(key),
                value: formatValue(value)
            }));

        if (entries.length === 0) return null;

        return (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                {entries.map((item, i) => (
                    <div key={i} className="flex items-start gap-4 p-6 bg-gradient-to-br from-gray-50 to-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg hover:border-black/10 transition-all duration-300">
                        <div className="flex-shrink-0 w-12 h-12 bg-black rounded-xl flex items-center justify-center text-2xl shadow-md">
                            <FaCheck className="text-white text-lg" aria-hidden />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-[9px] font-poppinsSemiBold uppercase tracking-widest text-gray-400 mb-1">{item.label}</p>
                            <p className="text-black font-poppinsMedium text-sm leading-relaxed break-words">{item.value}</p>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    if (loading) return (
        <div className="min-h-screen bg-white flex flex-col items-center justify-center space-y-4">
            <div className="w-12 h-12 border-4 border-black border-t-transparent rounded-full animate-spin"></div>
            <p className="text-xs font-poppinsSemiBold uppercase tracking-widest text-black">Loading experience...</p>
        </div>
    );

    if (error || !experience) return (
        <div className="min-h-screen bg-white flex flex-col items-center justify-center p-8">
            <h2 className="text-2xl sm:text-3xl font-AXONALLPrimeRegular font-semibold text-black mb-4">Experience not found</h2>
            <button onClick={() => navigate('/experiences')} className="px-8 py-4 bg-black text-white font-AXONALLPrimeRegular uppercase tracking-widest text-xs rounded-xl">Back to experiences</button>
        </div>
    );

    return (
        <MainLayout>
            {/* Hidden offscreen container for PDF generation */}
            <div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
                <div id="experience-pdf-template">
                    <ExperiencePdfTemplate experience={experience} />
                </div>
            </div>

            <div id="experience-details-content" className="min-h-screen bg-white font-poppinsRegular text-[15px] text-neutral-800 antialiased selection:bg-black selection:text-white">
            <style>
                {`
                    .experience-booking-scrollbar::-webkit-scrollbar {
                        width: 4px;
                    }
                    .experience-booking-scrollbar::-webkit-scrollbar-track {
                        background: transparent;
                    }
                    .experience-booking-scrollbar::-webkit-scrollbar-thumb {
                        background: #ddd;
                        border-radius: 10px;
                    }
                    .experience-booking-scrollbar::-webkit-scrollbar-thumb:hover {
                        background: #ccc;
                    }
                    .experience-booking-scrollbar {
                        scrollbar-width: thin;
                        scrollbar-color: #ddd transparent;
                    }
                `}
            </style>
            {/* HERO SECTION */}
            <div className="relative h-[85vh] min-h-[600px] flex items-center overflow-hidden bg-black">
                {/* Background Image */}
                <div className="absolute inset-0 z-0">
                    <img
                        src={getCoverImage()}
                        className="w-full h-full object-cover opacity-60 scale-105"
                        alt="Hero background"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-black via-black/40 to-transparent"></div>
                    {/* Smooth transition gradient */}
                    <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-white to-transparent z-10"></div>
                    <div className="absolute inset-0 backdrop-blur-[2px]"></div>
                </div>

                {/* Hero Content */}
                <div className="relative z-10 max-w-[1600px] mx-auto w-full px-6 lg:px-12 pt-24 pb-12 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

                    {/* Left: Text & CTA */}
                    <div className="space-y-8 animate-in fade-in slide-in-from-left duration-1000">
                        <div className="space-y-2">
                            <p className="text-white/60 font-poppinsSemiBold uppercase tracking-[0.3em] text-[9px] flex items-center gap-2">
                                <span className="w-8 h-px bg-white/30"></span>
                                {experience.category ? stripEmojis(experience.category) : ''}
                            </p>
                            <h1 className="text-3xl md:text-4xl lg:text-5xl font-AXONALLPrimeRegular font-medium text-white leading-[1.12] tracking-tight">
                                {stripEmojis(experience.title)}
                            </h1>
                        </div>

                        <div
                            className="text-white/75 text-sm md:text-base font-poppinsLight leading-relaxed max-w-lg line-clamp-3 quill-content"
                                dangerouslySetInnerHTML={{ __html: stripEmojis(experience.description || '') }}
                        />

                        <div className="flex flex-wrap gap-4 pt-4" data-html2canvas-ignore="true">
                            <button
                                onClick={handleAddToCart}
                                disabled={isAddingToCart}
                                className="px-8 py-4 bg-white text-black font-AXONALLPrimeRegular uppercase tracking-widest text-[11px] rounded-full shadow-2xl hover:scale-[1.02] active:scale-95 transition-all flex items-center gap-3 group disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <FaCalendarAlt className="group-hover:rotate-12 transition-transform text-sm" />
                                {isAddingToCart ? 'Adding...' : 'Add to cart'}
                            </button>
                            {getCoverVideo() && !isInlinePlaying && (
                                <button
                                    onClick={() => setShowVideoModal(true)}
                                    className="px-7 py-4 bg-black border border-white/20 text-white font-AXONALLPrimeRegular uppercase tracking-[0.2em] text-[10px] rounded-full hover:bg-white/10 transition-all flex items-center gap-3 shadow-2xl"
                                >
                                    <FaPlay className="text-[10px]" />
                                    Watch Video
                                </button>
                            )}
                            <button
                                onClick={handleDownloadPdf}
                                disabled={isDownloadingPdf}
                                className="px-7 py-4 bg-black border border-white/20 text-white font-AXONALLPrimeRegular uppercase tracking-[0.2em] text-[10px] rounded-full hover:bg-white/10 transition-all flex items-center gap-3 shadow-2xl"
                            >
                                {isDownloadingPdf ? (
                                    <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                ) : (
                                    <FaDownload className="text-[10px]" />
                                )}
                                {isDownloadingPdf ? 'Generating...' : 'Download'}
                            </button>
                        </div>
                    </div>

                    {/* Right: Video Card */}
                    <div className="hidden lg:block relative group animate-in fade-in slide-in-from-right duration-1000">
                        <div className="relative aspect-video rounded-[2rem] overflow-hidden shadow-[0_50px_100px_-20px_rgba(0,0,0,0.5)] border border-white/10 group-hover:border-white/30 transition-colors bg-black">
                            {isInlinePlaying && getCoverVideo() ? (
                                <video
                                    src={getCoverVideo()}
                                    className="absolute inset-0 w-full h-full object-cover"
                                    controls
                                    autoPlay
                                />
                            ) : (
                                <>
                                    <img
                                        src={getCoverImage()}
                                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                        alt="Video preview"
                                    />
                                    <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                                        <div className="w-20 h-20 bg-white/20 backdrop-blur-xl rounded-full flex items-center justify-center border border-white/30 group-hover:scale-110 group-hover:bg-white/40 transition-all cursor-pointer shadow-2xl" onClick={() => setIsInlinePlaying(true)}>
                                            <FaPlay className="text-white text-2xl ml-1" />
                                        </div>
                                    </div>
                                    <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent flex items-center gap-4 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <FaPlay className="text-white text-xs" />
                                        <div className="flex-1 h-1 bg-white/20 rounded-full relative">
                                            <div className="absolute left-0 top-0 bottom-0 w-1/3 bg-white rounded-full"></div>
                                        </div>
                                        <span className="text-[10px] text-white font-mono">0:00 / 0:45</span>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* CONTENT AREA */}
            <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-16 sm:py-20">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 lg:gap-16">

                    {/* Left: Detailed Info */}
                    <div className="lg:col-span-2 space-y-14 sm:space-y-16">

                        {/* Overview */}
                        <section className="space-y-8">
                            <div className="space-y-4">
                                <h2 className="text-2xl sm:text-3xl font-AXONALLPrimeRegular font-semibold text-black tracking-tight uppercase">Overview</h2>
                                <div className="h-1 w-20 bg-black"></div>
                            </div>
                            <div
                                className="text-gray-600 text-sm sm:text-[15px] font-poppinsRegular leading-relaxed prose prose-sm max-w-none quill-content [&_p]:text-inherit [&_li]:text-inherit"
                                    dangerouslySetInnerHTML={{ __html: stripEmojis(experience.description || '') }}
                            />
                        </section>

                        {/* Category Specific Details */}
                        {experience?.categorySpecific && Object.keys(experience.categorySpecific).length > 0 && (
                            <section className="space-y-8">
                                <div className="space-y-4">
                                    <h2 className="text-2xl sm:text-3xl font-AXONALLPrimeRegular font-semibold text-black tracking-tight uppercase">Experience Specifics</h2>
                                    <div className="h-1 w-20 bg-black"></div>
                                </div>
                                {renderCategoryContent()}
                            </section>
                        )}


                        {/* Highlights */}
                        {experience.highlights?.length > 0 && (
                            <section className="space-y-8">
                                <div className="space-y-4">
                                    <h2 className="text-2xl sm:text-3xl font-AXONALLPrimeRegular font-semibold text-black tracking-tight uppercase">Highlights</h2>
                                    <div className="h-1 w-20 bg-black"></div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {experience.highlights.map((highlight, i) => (
                                        <div key={i} className="flex items-start gap-4 p-5 bg-gradient-to-r from-black to-gray-800 rounded-2xl text-white shadow-lg hover:shadow-2xl transition-shadow">
                                            <div className="flex-shrink-0 w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center text-sm border border-white/20">
                                                <FaCheck className="text-white text-sm" aria-hidden />
                                            </div>
                                            <p className="text-white font-poppinsRegular text-sm leading-relaxed">{stripEmojis(String(highlight))}</p>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        )}

                        {/* Image Gallery - The Visual Narrative */}
                        {experience.images?.length > 0 && (
                            <section className="space-y-8 sm:space-y-10">
                                <div className="space-y-4">
                                    <h2 className="text-2xl sm:text-3xl font-AXONALLPrimeRegular font-semibold text-black tracking-tight uppercase">The Visual Narrative</h2>
                                    <div className="h-1 w-20 bg-black"></div>
                                </div>

                                <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                                    {experience.images.slice(0, 5).map((image, index) => (
                                        <div
                                            key={index}
                                            onClick={() => {
                                                setSelectedGalleryIndex(index);
                                                setShowFullGallery(true);
                                            }}
                                            className={`relative group overflow-hidden rounded-[2rem] shadow-sm hover:shadow-2xl transition-all duration-700 cursor-pointer ${index === 0 ? 'col-span-2 row-span-2' :
                                                index === 1 ? 'col-span-2' : ''
                                                }`}
                                            style={{ minHeight: index === 0 ? '500px' : '240px' }}
                                        >
                                            <img
                                                src={image.url || image}
                                                alt={`Experience ${index + 1}`}
                                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000 ease-out"
                                            />

                                            {/* Minimalist Overlay */}
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-8">
                                                <div className="text-white transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500 delay-100">
                                                    <p className="font-poppinsSemiBold text-[10px] uppercase tracking-[0.3em] mb-1">
                                                        {image.isCover ? 'Primary Perspective' : `Moment 0${index + 1}`}
                                                    </p>
                                                    <div className="w-10 h-px bg-white/50"></div>
                                                </div>
                                            </div>

                                            {/* Subtle Featured Badge */}
                                            {image.isCover && (
                                                <div className="absolute top-6 left-6 bg-white/90 backdrop-blur-md text-black px-4 py-2 rounded-full text-[9px] font-poppinsSemiBold uppercase tracking-widest shadow-xl">
                                                    Curated Cover
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                {/* View All Button - Now Functional */}
                                {experience.images.length > 5 && (
                                    <div className="flex justify-center pt-4">
                                        <button
                                            onClick={() => {
                                                setSelectedGalleryIndex(0);
                                                setShowFullGallery(true);
                                            }}
                                            className="group relative px-10 py-4 bg-white border-2 border-black text-black font-AXONALLPrimeRegular uppercase tracking-widest text-[10px] rounded-full overflow-hidden hover:text-white transition-colors duration-500"
                                        >
                                            <span className="relative z-10">Archive All {experience.images.length} Visuals</span>
                                            <div className="absolute inset-0 bg-black translate-y-full group-hover:translate-y-0 transition-transform duration-500"></div>
                                        </button>
                                    </div>
                                )}
                            </section>
                        )}

                        {/* Itinerary - Unified handling */}
                        {(() => {
                            const itinData = processItinerary();

                            if (!itinData.hasItinerary) return null;

                            return (
                                <section className="space-y-8">
                                    <div className="space-y-4">
                                        <h2 className="text-2xl sm:text-3xl font-AXONALLPrimeRegular font-semibold text-black tracking-tight uppercase">Journey Itinerary</h2>
                                        <div className="h-1 w-20 bg-black"></div>
                                    </div>

                                    {/* Structured Itinerary */}
                                    {!itinData.isHTML && itinData.structured && (
                                        <div className="space-y-8 relative before:absolute before:left-8 before:top-4 before:bottom-4 before:w-0.5 before:bg-gradient-to-b before:from-black before:via-gray-300 before:to-black">
                                            {itinData.structured
                                                .sort((a, b) => (a.step || 0) - (b.step || 0))
                                                .map((step, idx) => (
                                                    <div key={idx} className="relative pl-24 group">
                                                        <div className="absolute left-0 top-0 w-14 h-14 bg-white border-2 border-black flex items-center justify-center text-black font-AXONALLPrimeRegular text-lg font-semibold rounded-2xl group-hover:bg-black group-hover:text-white transition-all shadow-xl z-10">
                                                            {step.step || idx + 1}
                                                        </div>
                                                        <div className="space-y-3 p-5 sm:p-6 bg-gradient-to-br from-gray-50 to-white rounded-2xl border border-gray-100 hover:shadow-lg transition-all duration-300">
                                                            <div className="flex items-start justify-between gap-4 flex-wrap">
                                                                <h4 className="text-lg sm:text-xl font-AXONALLPrimeRegular font-semibold text-black flex-1">{step.title ? stripEmojis(String(step.title)) : ''}</h4>
                                                                {step.duration && (
                                                                    <span className="inline-flex items-center gap-2 text-[9px] font-poppinsSemiBold uppercase text-gray-500 bg-gray-100 px-3 py-1.5 rounded-full border border-gray-200">
                                                                        <FaClock className="text-gray-500 text-xs shrink-0" aria-hidden />
                                                                        {stripEmojis(String(step.duration))}
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {step.description && (
                                                                <p className="text-gray-600 font-poppinsRegular text-sm leading-relaxed">{stripEmojis(String(step.description))}</p>
                                                            )}
                                                            {step.location && (
                                                                <div className="flex items-center gap-2 text-black/60 text-sm font-poppinsMedium">
                                                                    <FaMapMarkerAlt className="text-black shrink-0" aria-hidden /> {stripEmojis(String(step.location))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                        </div>
                                    )}

                                    {/* HTML Itinerary (from React Quill or character-indexed) */}
                                    {itinData.isHTML && itinData.content && (
                                        <div className="relative pl-8 md:pl-12">
                                            {/* Decorative Timeline Line */}
                                            <div className="absolute left-0 top-4 bottom-4 w-1 bg-gradient-to-b from-black via-gray-200 to-black rounded-full shadow-sm"></div>

                                            <div
                                                className="prose prose-sm max-w-none quill-content p-7 md:p-10 bg-white rounded-[2rem] border border-gray-100 shadow-[0_20px_50px_rgba(0,0,0,0.05)] font-poppinsRegular
                                                [&>p]:text-gray-600 [&>p]:text-sm [&>p]:leading-relaxed [&>p]:mb-5
                                                [&>h1]:text-black [&>h1]:font-AXONALLPrimeRegular [&>h1]:font-semibold [&>h1]:text-xl [&>h1]:uppercase [&>h1]:tracking-tight [&>h1]:mb-6
                                                [&>h2]:text-black [&>h2]:font-AXONALLPrimeRegular [&>h2]:font-semibold [&>h2]:text-lg [&>h2]:uppercase [&>h2]:tracking-tight [&>h2]:mb-6
                                                [&>h3]:text-black [&>h3]:font-AXONALLPrimeRegular [&>h3]:font-semibold [&>h3]:text-base [&>h3]:uppercase [&>h3]:tracking-tight [&>h3]:mb-5
                                                [&>strong]:text-black [&>strong]:font-poppinsSemiBold
                                                [&>ul]:list-none [&>ul]:pl-0 [&>ul]:space-y-3
                                                [&>ul>li]:relative [&>ul>li]:pl-8 [&>ul>li]:text-gray-600 [&>ul>li]:text-sm
                                                [&>ul>li]:before:absolute [&>ul>li]:before:left-0 [&>ul>li]:before:top-2.5 [&>ul>li]:before:w-2 [&>ul>li]:before:h-2 [&>ul>li]:before:bg-black [&>ul>li]:before:rounded-full
                                                [&>p>strong:first-child]:block [&>p>strong:first-child]:text-lg [&>p>strong:first-child]:font-AXONALLPrimeRegular [&>p>strong:first-child]:text-black [&>p>strong:first-child]:mb-2 [&>p>strong:first-child]:uppercase [&>p>strong:first-child]:tracking-wide"
                                                dangerouslySetInnerHTML={{ __html: stripEmojis(itinData.content) }}
                                            />
                                        </div>
                                    )}
                                </section>
                            );
                        })()}

                        {/* Requirements */}
                        {experience.requirements && typeof experience.requirements === 'object' && Object.values(experience.requirements).some(v => v && (Array.isArray(v) ? v.length > 0 : true)) && (
                            <section className="space-y-8">
                                <div className="space-y-4">
                                    <h2 className="text-2xl sm:text-3xl font-AXONALLPrimeRegular font-semibold text-black tracking-tight uppercase">Requirements</h2>
                                    <div className="h-1 w-20 bg-black"></div>
                                </div>
                                <div className="space-y-4">
                                    {experience.requirements.physicalFitness && (
                                        <div className="p-5 bg-gradient-to-br from-blue-50 to-white rounded-2xl border border-blue-100">
                                            <p className="text-[9px] font-poppinsSemiBold uppercase tracking-widest text-blue-600 mb-2">Physical fitness</p>
                                            <p className="text-black font-poppinsRegular text-sm">{stripEmojis(String(experience.requirements.physicalFitness))}</p>
                                        </div>
                                    )}
                                    {experience.requirements.equipment?.length > 0 && (
                                            <div className="p-5 bg-gradient-to-br from-slate-50 to-white rounded-2xl border border-slate-200">
                                                <p className="text-[9px] font-poppinsSemiBold uppercase tracking-widest text-slate-600 mb-2">Equipment needed</p>
                                            <p className="text-black font-poppinsRegular text-sm">{stripEmojis(experience.requirements.equipment.join(', '))}</p>
                                        </div>
                                    )}
                                    {experience.requirements.specialSkills?.length > 0 && (
                                        <div className="p-5 bg-gradient-to-br from-green-50 to-white rounded-2xl border border-green-100">
                                            <p className="text-[9px] font-poppinsSemiBold uppercase tracking-widest text-green-600 mb-2">Special skills</p>
                                            <p className="text-black font-poppinsRegular text-sm">{stripEmojis(experience.requirements.specialSkills.join(', '))}</p>
                                        </div>
                                    )}
                                    {experience.requirements.medicalConditions?.length > 0 && (
                                        <div className="p-5 bg-gradient-to-br from-rose-50 to-white rounded-2xl border border-rose-100">
                                            <p className="text-[9px] font-poppinsSemiBold uppercase tracking-widest text-rose-600 mb-2">Medical considerations</p>
                                            <p className="text-black font-poppinsRegular text-sm">{stripEmojis(experience.requirements.medicalConditions.join(', '))}</p>
                                        </div>
                                    )}
                                </div>
                            </section>
                        )}

                        {/* Inclusions/Exclusions */}
                        <section className="space-y-8">
                            <div className="space-y-4">
                                <h2 className="text-2xl sm:text-3xl font-AXONALLPrimeRegular font-semibold text-black tracking-tight uppercase">What's Included</h2>
                                <div className="h-1 w-20 bg-black"></div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                {/* Included */}
                                {experience.included?.length > 0 && (
                                    <div className="p-8 bg-gradient-to-br from-emerald-50 to-white rounded-3xl border border-emerald-100 shadow-sm hover:shadow-lg transition-all">
                                        <h3 className="text-lg font-AXONALLPrimeRegular font-semibold mb-5 uppercase tracking-widest flex items-center gap-2 text-emerald-700">
                                            <div className="w-8 h-8 bg-emerald-600 rounded-lg flex items-center justify-center">
                                                <FaCheck className="text-white text-sm" />
                                            </div>
                                            Included
                                        </h3>
                                        <ul className="space-y-3">
                                            {experience.included.map((item, i) => (
                                                <li key={i} className="flex gap-3 text-gray-700 font-poppinsRegular text-sm items-start">
                                                    <span className="w-2 h-2 bg-emerald-500 rounded-full mt-2 flex-shrink-0"></span>
                                                    <span>{stripEmojis(String(item))}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {/* Not Included or Excluded */}
                                {(experience.notIncluded?.length > 0 || experience.excluded?.length > 0) && (
                                    <div className="p-8 bg-gradient-to-br from-gray-50 to-white rounded-3xl border border-gray-200 shadow-sm hover:shadow-lg transition-all">
                                        <h3 className="text-lg font-AXONALLPrimeRegular font-semibold mb-5 uppercase tracking-widest flex items-center gap-2 text-gray-600">
                                            <div className="w-8 h-8 bg-gray-400 rounded-lg flex items-center justify-center">
                                                <FaTimes className="text-white text-sm" />
                                            </div>
                                            Not Included
                                        </h3>
                                        <ul className="space-y-3">
                                            {(experience.notIncluded || experience.excluded || []).map((item, i) => (
                                                <li key={i} className="flex gap-3 text-gray-500 font-poppinsRegular text-sm items-start">
                                                    <span className="w-2 h-2 bg-gray-300 rounded-full mt-2 flex-shrink-0"></span>
                                                    <span>{stripEmojis(String(item))}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </section>

                        {/* Safety Guidelines */}
                        {experience.safetyGuidelines?.length > 0 && (
                            <section className="space-y-8">
                                <div className="space-y-4">
                                    <h2 className="text-2xl sm:text-3xl font-AXONALLPrimeRegular font-semibold text-black tracking-tight uppercase">Safety Guidelines</h2>
                                    <div className="h-1 w-20 bg-black"></div>
                                </div>
                                <div className="p-8 bg-gradient-to-br from-yellow-50 to-white rounded-3xl border-2 border-yellow-200">
                                    <div className="flex items-center gap-3 mb-6">
                                        <div className="w-10 h-10 bg-yellow-500 rounded-xl flex items-center justify-center">
                                            <FaShieldAlt className="text-white text-xl" />
                                        </div>
                                        <p className="text-yellow-800 font-AXONALLPrimeRegular font-semibold uppercase text-xs tracking-widest">Please read carefully</p>
                                    </div>
                                    <ul className="space-y-3">
                                        {experience.safetyGuidelines.map((guideline, i) => (
                                            <li key={i} className="flex gap-3 text-gray-700 font-poppinsRegular text-sm items-start">
                                                <FaExclamationTriangle className="text-yellow-600 mt-1 flex-shrink-0" aria-hidden />
                                                <span>{stripEmojis(String(guideline))}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </section>
                        )}

                        {/* Terms & Conditions */}
                        {experience.termsConditions?.length > 0 && (
                            <section className="space-y-8">
                                <div className="space-y-4">
                                    <h2 className="text-2xl sm:text-3xl font-AXONALLPrimeRegular font-semibold text-black tracking-tight uppercase">Terms & Conditions</h2>
                                    <div className="h-1 w-20 bg-black"></div>
                                </div>
                                <div className="p-8 bg-gradient-to-br from-gray-100 to-white rounded-3xl border border-gray-200">
                                    <ul className="space-y-3">
                                        {experience.termsConditions.map((term, i) => (
                                            <li key={i} className="flex gap-3 text-gray-600 font-poppinsRegular text-sm items-start leading-relaxed">
                                                <span className="text-black font-poppinsSemiBold flex-shrink-0">{i + 1}.</span>
                                                <span>{stripEmojis(String(term))}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </section>
                        )}

                        {/* Essential Details Grid */}
                        <section className="space-y-8">
                            <div className="space-y-4">
                                <h2 className="text-2xl sm:text-3xl font-AXONALLPrimeRegular font-semibold text-black tracking-tight uppercase">Essential Details</h2>
                                <div className="h-1 w-20 bg-black"></div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Location */}
                                {experience.location && (
                                    <div className="p-6 bg-gradient-to-br from-gray-50 to-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300">
                                        <div className="flex items-start gap-4">
                                            <div className="flex-shrink-0 w-12 h-12 bg-black rounded-xl flex items-center justify-center shadow-md">
                                                <FaMapMarkerAlt className="text-white text-xl" />
                                            </div>
                                            <div>
                                                <p className="text-[9px] font-poppinsSemiBold uppercase tracking-widest text-gray-400 mb-1">Location</p>
                                                <p className="text-black font-poppinsMedium text-sm">{stripEmojis(`${experience.location.city}, ${experience.location.country}`)}</p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Difficulty */}
                                {experience.difficulty && (
                                    <div className="p-6 bg-gradient-to-br from-gray-50 to-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300">
                                        <div className="flex items-start gap-4">
                                            <div className="flex-shrink-0 w-12 h-12 bg-black rounded-xl flex items-center justify-center shadow-md">
                                                    <FaBolt className="text-white text-xl" aria-hidden />
                                            </div>
                                            <div>
                                                <p className="text-[9px] font-poppinsSemiBold uppercase tracking-widest text-gray-400 mb-1">Difficulty</p>
                                                <p className="text-black font-poppinsMedium text-sm capitalize">{experience.difficulty ? stripEmojis(String(experience.difficulty)) : ''}</p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Age Restrictions */}
                                {(experience.ageRestriction?.minAge || experience.ageRestriction?.maxAge) && (
                                    <div className="p-6 bg-gradient-to-br from-gray-50 to-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300">
                                        <div className="flex items-start gap-4">
                                            <div className="flex-shrink-0 w-12 h-12 bg-black rounded-xl flex items-center justify-center shadow-md">
                                                <FaChild className="text-white text-xl" />
                                            </div>
                                            <div>
                                                <p className="text-[9px] font-poppinsSemiBold uppercase tracking-widest text-gray-400 mb-1">Age range</p>
                                                <p className="text-black font-poppinsMedium text-sm">
                                                    {experience.ageRestriction.minAge || 0} - {experience.ageRestriction.maxAge || '100+'} years
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Lead Time */}
                                {experience.leadTime > 0 && (
                                    <div className="p-6 bg-gradient-to-br from-gray-50 to-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300">
                                        <div className="flex items-start gap-4">
                                            <div className="flex-shrink-0 w-12 h-12 bg-black rounded-xl flex items-center justify-center shadow-md">
                                                <FaClock className="text-white text-xl" />
                                            </div>
                                            <div>
                                                <p className="text-[9px] font-poppinsSemiBold uppercase tracking-widest text-gray-400 mb-1">Booking lead time</p>
                                                <p className="text-black font-poppinsMedium text-sm">{experience.leadTime} days in advance</p>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Languages */}
                                {experience.languages?.length > 0 && (
                                    <div className="p-6 bg-gradient-to-br from-gray-50 to-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300">
                                        <div className="flex items-start gap-4">
                                            <div className="flex-shrink-0 w-12 h-12 bg-black rounded-xl flex items-center justify-center shadow-md">
                                                <FaGlobe className="text-white text-xl" />
                                            </div>
                                            <div>
                                                <p className="text-[9px] font-poppinsSemiBold uppercase tracking-widest text-gray-400 mb-1">Languages</p>
                                                <p className="text-black font-poppinsMedium text-sm">{stripEmojis(experience.languages.join(', '))}</p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </section>
                    </div>

                    {/* Right: Sticky booking shell + inner scroll (sticky must not be on the overflow element) */}
                    <div className="lg:col-span-1" data-html2canvas-ignore="true">
                        <div className="sticky top-24 z-10 pb-10">
                            <div className="max-h-[calc(100vh-7rem)] overflow-y-auto pr-1 -mr-1 experience-booking-scrollbar overscroll-y-contain">
                            {/* Booking Card */}
                            <div className="bg-white rounded-[2rem] sm:rounded-[2.5rem] shadow-[0_30px_100px_rgba(0,0,0,0.12)] border-2 border-gray-100 p-7 sm:p-9 overflow-hidden relative">
                                <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-black via-gray-800 to-black"></div>

                                <div className="text-center mb-7">
                                    <div className="text-[9px] font-poppinsSemiBold text-gray-400 uppercase tracking-[0.2em] mb-2">Price overview</div>
                                    <div className="text-3xl sm:text-4xl font-AXONALLPrimeRegular font-semibold text-black mb-1">{formatPriceDisplay(experience.pricing)}</div>
                                    <div className="text-[11px] font-poppinsMedium text-gray-500">Per {experience.pricing?.priceType === 'per_person' ? 'guest' : experience.pricing?.priceType === 'per_group' ? 'group' : 'booking'}</div>
                                </div>

                                <div className="grid grid-cols-3 gap-2 mb-7 p-3 bg-gray-50 rounded-2xl border border-gray-100">
                                    <div className="text-center p-2">
                                        <p className="text-[8px] font-poppinsSemiBold uppercase tracking-widest text-gray-400 mb-1">Duration</p>
                                        <p className="text-black font-poppinsMedium text-[10px] leading-tight">{formatDuration(experience.duration)}</p>
                                    </div>
                                    <div className="text-center p-2 border-x border-gray-200">
                                        <p className="text-[8px] font-poppinsSemiBold uppercase tracking-widest text-gray-400 mb-1">Group</p>
                                        <p className="text-black font-poppinsMedium text-[10px]">
                                            {experience.pricing?.minParticipants || experience.capacity?.minParticipants || 1}-{experience.pricing?.maxParticipants || experience.capacity?.maxParticipants || 12}
                                        </p>
                                    </div>
                                    <div className="text-center p-2">
                                        <p className="text-[8px] font-poppinsSemiBold uppercase tracking-widest text-gray-400 mb-1">Cancel</p>
                                        <p className="text-black font-poppinsMedium text-[10px] truncate px-1 capitalize" title={experience.cancellationPolicy?.replace('_', ' ')}>
                                            {experience.cancellationPolicy?.split('_')[0]}
                                        </p>
                                    </div>
                                </div>

                                {/* Participant Counter */}
                                <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-5 space-y-4 mb-6 border border-gray-100">
                                    <div className="flex items-center justify-between">
                                        <span className="text-[9px] font-poppinsSemiBold uppercase text-gray-500 tracking-widest">Guests</span>
                                        <div className="flex items-center gap-3">
                                            <button
                                                onClick={() => setParticipants(p => Math.max(experience.pricing?.minParticipants || experience.capacity?.minParticipants || 1, p - 1))}
                                                className="w-9 h-9 rounded-full bg-white border-2 border-gray-200 flex items-center justify-center font-poppinsSemiBold text-sm text-black hover:bg-black hover:text-white hover:border-black transition-all shadow-sm"
                                            >-</button>
                                            <span className="text-xl font-AXONALLPrimeRegular font-semibold text-black w-8 text-center">{participants}</span>
                                            <button
                                                onClick={() => setParticipants(p => Math.min(experience.pricing?.maxParticipants || experience.capacity?.maxParticipants || 12, p + 1))}
                                                className="w-9 h-9 rounded-full bg-white border-2 border-gray-200 flex items-center justify-center font-poppinsSemiBold text-sm text-black hover:bg-black hover:text-white hover:border-black transition-all shadow-sm"
                                            >+</button>
                                        </div>
                                    </div>
                                    <div className="pt-4 border-t-2 border-gray-200 space-y-2">
                                        <div className="flex items-center justify-between text-gray-500">
                                            <span className="text-[9px] font-poppinsSemiBold uppercase tracking-widest">Subtotal</span>
                                            <span className="text-sm font-poppinsSemiBold">
                                                {formatPrice(
                                                    ((experience.basePrice || experience.pricing?.basePrice || 0) + (experience.commissionAmount || 0)) * (experience.pricing?.priceType === 'per_group' ? 1 : participants),
                                                    experience.pricing?.currency
                                                )}
                                            </span>
                                        </div>
                                        {processingFeeData && (
                                            <div className="flex items-center justify-between text-gray-500">
                                                <span className="text-[9px] font-poppinsSemiBold uppercase tracking-widest">Processing fee</span>
                                                <span className="text-sm font-poppinsSemiBold">
                                                    {formatPrice(processingFeeData.totalProcessingFees, experience.pricing?.currency)}
                                                </span>
                                            </div>
                                        )}
                                        <div className="flex items-center justify-between pt-2">
                                            <span className="text-[11px] font-AXONALLPrimeRegular font-semibold uppercase text-black">Total</span>
                                            <span className={`text-2xl font-AXONALLPrimeRegular font-semibold text-black ${isCalculatingFees ? 'opacity-50' : ''}`}>
                                                {formatPrice(calculateTotal(), experience.pricing?.currency)}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Primary Call to Action - Moved Up */}
                                <div className="space-y-3 mb-8">
                                    <button
                                        onClick={handleAddToCart}
                                        disabled={isAddingToCart}
                                        className="w-full py-4 bg-black text-white font-AXONALLPrimeRegular uppercase tracking-widest text-[10px] rounded-2xl shadow-xl hover:shadow-2xl hover:bg-gray-900 active:scale-[0.98] transition-all flex items-center justify-center gap-3 group disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <FaCalendarAlt className="text-sm group-hover:rotate-12 transition-transform" />
                                        {isAddingToCart ? 'Adding...' : (selectedDate && (availableTimeSlots.length === 0 || selectedTimeSlot) ? 'Add to cart' : 'Select date and time')}
                                    </button>
                                </div>

                                {/* Check Availability Section */}
                                <div id="availability-section" className="space-y-6 mb-8">
                                    <div className="flex items-center justify-between">
                                        <h3 className="text-[10px] font-AXONALLPrimeRegular font-semibold uppercase text-black tracking-[0.28em] flex items-center gap-3">
                                            <span className="w-8 h-px bg-blue-600"></span>
                                            Availability
                                        </h3>
                                        {experience.leadTime > 0 && (
                                            <div className="flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 text-blue-700 rounded-full text-[8px] font-poppinsSemiBold uppercase tracking-wider border border-blue-100">
                                                <FaClock className="text-[10px]" aria-hidden />
                                                {experience.leadTime} day lead
                                            </div>
                                        )}
                                    </div>

                                    {/* Premium Date Strip Picker */}
                                    {availableDates.length > 0 ? (
                                        <div className="space-y-5">
                                            <div className="flex flex-col gap-4">
                                                <div className="flex items-center justify-between">
                                                    <span className="text-[9px] font-poppinsSemiBold text-gray-400 uppercase tracking-[0.2em]">Select date</span>
                                                    <div className="flex items-center gap-3">
                                                        {availableDates.length > 15 && (
                                                            <div className="flex items-center gap-2 px-3 py-1 bg-gray-50 rounded-full border border-gray-100">
                                                                <span className="text-[8px] font-poppinsSemiBold text-gray-400">Jump to</span>
                                                                <select
                                                                    className="bg-transparent text-[9px] font-poppinsSemiBold text-black outline-none border-none cursor-pointer"
                                                                    onChange={(e) => scrollToMonth(e.target.value)}
                                                                >
                                                                    <option value="">Month</option>
                                                                    {getAvailableMonths().map(m => (
                                                                        <option key={m} value={m}>{m}</option>
                                                                    ))}
                                                                </select>
                                                            </div>
                                                        )}
                                                        {selectedDate && (
                                                            <button
                                                                onClick={() => setSelectedDate('')}
                                                                className="text-[9px] font-poppinsSemiBold text-blue-600 uppercase tracking-widest hover:underline decoration-blue-200 underline-offset-4"
                                                            >
                                                                Reset
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            <div
                                                ref={dateStripRef}
                                                className="flex gap-4 overflow-x-auto pb-6 no-scrollbar -mx-2 px-2 snap-x"
                                            >
                                                {availableDates.map((dateObj, index) => {
                                                    const date = new Date(dateObj.date);
                                                    const isSelected = selectedDate === dateObj.date;
                                                    const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
                                                    const dayNum = date.getDate();
                                                    const monthName = date.toLocaleDateString('en-US', { month: 'short' });

                                                    return (
                                                        <motion.button
                                                            key={index}
                                                            whileHover={{ y: -4 }}
                                                            whileTap={{ scale: 0.95 }}
                                                            onClick={() => handleDateChange(dateObj.date)}
                                                            className={`flex-shrink-0 w-22 h-28 rounded-[2.5rem] flex flex-col items-center justify-center gap-2 transition-all duration-500 snap-center border-2 ${isSelected
                                                                ? 'bg-black border-black text-white shadow-2xl shadow-black/30'
                                                                : 'bg-white border-gray-100 text-black hover:border-blue-100 hover:bg-blue-50/20'
                                                                }`}
                                                            style={{ width: '88px', height: '112px' }}
                                                        >
                                                            <span className={`text-[9px] font-poppinsSemiBold uppercase tracking-widest ${isSelected ? 'text-white/50' : 'text-gray-300'}`}>
                                                                {dayName}
                                                            </span>
                                                            <span className="text-2xl font-AXONALLPrimeRegular font-semibold tracking-tight">
                                                                {dayNum}
                                                            </span>
                                                            <span className={`text-[9px] font-poppinsSemiBold uppercase tracking-widest ${isSelected ? 'text-white/80' : 'text-gray-500'}`}>
                                                                {monthName}
                                                            </span>
                                                            {isSelected && (
                                                                <motion.div
                                                                    layoutId="activeMomentDot"
                                                                    className="w-1.5 h-1.5 bg-blue-500 rounded-full"
                                                                />
                                                            )}
                                                        </motion.button>
                                                    );
                                                })}
                                            </div>

                                            {/* Date Navigation Controls */}
                                            <div className="flex justify-between items-center pt-2">
                                                <div className="flex gap-3">
                                                    <button
                                                        onClick={() => scrollDateStrip('left')}
                                                        className="group w-12 h-12 rounded-2xl border border-gray-100 flex items-center justify-center text-gray-400 hover:bg-black hover:text-white hover:border-black transition-all shadow-sm active:scale-90"
                                                        aria-label="Previous dates"
                                                    >
                                                        <FaChevronLeft className="text-xs group-hover:-translate-x-0.5 transition-transform" />
                                                    </button>
                                                    <button
                                                        onClick={() => scrollDateStrip('right')}
                                                        className="group w-12 h-12 rounded-2xl border border-gray-100 flex items-center justify-center text-gray-400 hover:bg-black hover:text-white hover:border-black transition-all shadow-sm active:scale-90"
                                                        aria-label="Next dates"
                                                    >
                                                        <FaChevronRight className="text-xs group-hover:translate-x-0.5 transition-transform" />
                                                    </button>
                                                </div>

                                                {/* Sophisticated Scroll Progress Indicator */}
                                                <div className="flex items-center gap-3">
                                                    <div className="text-[8px] font-poppinsSemiBold text-gray-400 tracking-widest uppercase">
                                                        {availableDates.length} dates
                                                    </div>
                                                    <div className="w-16 h-1.5 bg-gray-50 rounded-full overflow-hidden border border-gray-100">
                                                        <motion.div
                                                            className="h-full bg-black"
                                                            initial={{ width: "10%" }}
                                                            animate={{
                                                                width: selectedDate
                                                                    ? `${((availableDates.findIndex(d => d.date === selectedDate) + 1) / availableDates.length) * 100}%`
                                                                    : "20%"
                                                            }}
                                                            transition={{ type: "spring", stiffness: 100, damping: 20 }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="p-8 bg-gray-50/50 rounded-[2.5rem] border-2 border-dashed border-gray-100 text-center">
                                            <FaInfoCircle className="mx-auto text-gray-300 mb-3 text-xl" />
                                            <p className="text-[9px] font-poppinsSemiBold text-gray-400 uppercase tracking-[0.2em] leading-relaxed">
                                                Availability on request <br /> contact concierge
                                            </p>
                                        </div>
                                    )}

                                    {/* Enhanced Time Slot Grid */}
                                    <AnimatePresence mode="wait">
                                        {selectedDate && availableTimeSlots.length > 0 && (
                                            <motion.div
                                                key="time-slots"
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: -10 }}
                                                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                                                className="space-y-5"
                                            >
                                                <span className="text-[9px] font-poppinsSemiBold text-gray-400 uppercase tracking-[0.2em]">Select time</span>
                                                <div className="grid grid-cols-1 gap-3">
                                                    {availableTimeSlots.map((timeSlot, index) => {
                                                        const isSelected = selectedTimeSlot === timeSlot;
                                                        return (
                                                            <motion.button
                                                                key={index}
                                                                whileHover={{ x: 6 }}
                                                                onClick={() => setSelectedTimeSlot(timeSlot)}
                                                                className={`p-6 rounded-[2rem] border-2 text-left transition-all duration-500 relative overflow-hidden group ${isSelected
                                                                    ? 'bg-blue-600 border-blue-600 text-white shadow-2xl shadow-blue-200'
                                                                    : 'bg-white border-gray-100 text-black hover:border-blue-100 hover:shadow-xl hover:shadow-gray-100'
                                                                    }`}
                                                            >
                                                                <div className="flex justify-between items-center relative z-10">
                                                                    <div className="space-y-1">
                                                                        <span className="font-poppinsSemiBold text-sm tracking-tight">
                                                                            {timeSlot.startTime} <span className="text-[9px] opacity-40 mx-2 uppercase font-poppinsSemiBold">to</span> {timeSlot.endTime}
                                                                        </span>
                                                                        {timeSlot.label && (
                                                                            <span className={`block text-[9px] font-poppinsSemiBold uppercase tracking-[0.15em] ${isSelected ? 'text-white/60' : 'text-gray-400'}`}>
                                                                                {stripEmojis(String(timeSlot.label))}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    {isSelected ? (
                                                                        <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center shadow-lg">
                                                                            <FaCheck className="text-blue-600 text-xs" />
                                                                        </div>
                                                                    ) : (
                                                                        <div className="w-8 h-8 rounded-full border-2 border-gray-100 group-hover:border-blue-100 flex items-center justify-center transition-all bg-gray-50/50">
                                                                            <div className="w-2 h-2 rounded-full bg-gray-200 group-hover:bg-blue-400 transition-colors" />
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                {isSelected && (
                                                                    <motion.div
                                                                        layoutId="highlight-bg"
                                                                        className="absolute inset-0 bg-gradient-to-r from-blue-600 to-blue-700"
                                                                        transition={{ duration: 0.6 }}
                                                                    />
                                                                )}
                                                            </motion.button>
                                                        );
                                                    })}
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>

                                {/* Quick Info */}
                                <div className="p-6 bg-gradient-to-br from-blue-50 to-white rounded-3xl border border-blue-100 flex items-start gap-4">
                                    <div className="p-3 bg-blue-600 rounded-xl shadow-md">
                                        <FaInfoCircle className="text-white text-xl" />
                                    </div>
                                    <div>
                                        <h4 className="font-AXONALLPrimeRegular font-semibold text-black uppercase text-[11px] mb-2">Need assistance?</h4>
                                        <p className="text-gray-600 text-sm font-poppinsRegular leading-relaxed">Our concierge team is available for personalized support and custom arrangements.</p>
                                    </div>
                                </div>

                            </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Image Gallery Modal */}
            {showFullGallery && (
                <div
                    className="fixed inset-0 z-[100] bg-black flex flex-col items-center justify-center animate-in fade-in duration-300"
                    onClick={() => setShowFullGallery(false)}
                >
                    <div className="absolute top-0 left-0 w-full p-8 flex justify-between items-center text-white z-10">
                        <div className="font-poppinsSemiBold text-[10px] uppercase tracking-widest">
                            Image {selectedGalleryIndex + 1} / {experience.images.length}
                        </div>
                        <button
                            onClick={() => setShowFullGallery(false)}
                            className="w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md flex items-center justify-center transition-all"
                        >
                            <FaTimes />
                        </button>
                    </div>

                    <div className="relative w-full h-full flex items-center justify-center p-4 md:p-12 overflow-hidden">
                        <AnimatePresence mode="wait">
                            <motion.img
                                key={selectedGalleryIndex}
                                initial={{ opacity: 0, scale: 0.9, x: 50 }}
                                animate={{ opacity: 1, scale: 1, x: 0 }}
                                exit={{ opacity: 0, scale: 1.1, x: -50 }}
                                transition={{ type: "spring", damping: 25, stiffness: 200 }}
                                src={experience.images[selectedGalleryIndex]?.url || experience.images[selectedGalleryIndex]}
                                className="max-w-full max-h-full object-contain shadow-2xl rounded-lg"
                                onClick={(e) => e.stopPropagation()}
                            />
                        </AnimatePresence>

                        <button
                            className="absolute left-6 w-14 h-14 rounded-full bg-white/5 hover:bg-white/20 backdrop-blur-sm flex items-center justify-center text-white transition-all group"
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedGalleryIndex(prev => prev === 0 ? experience.images.length - 1 : prev - 1);
                            }}
                        >
                            <FaChevronLeft className="group-hover:-translate-x-1 transition-transform" />
                        </button>
                        <button
                            className="absolute right-6 w-14 h-14 rounded-full bg-white/5 hover:bg-white/20 backdrop-blur-sm flex items-center justify-center text-white transition-all group"
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedGalleryIndex(prev => (prev + 1) % experience.images.length);
                            }}
                        >
                            <FaChevronRight className="group-hover:translate-x-1 transition-transform" />
                        </button>
                    </div>

                    <div className="absolute bottom-0 left-0 w-full p-6 bg-gradient-to-t from-black/80 to-transparent flex justify-center gap-2 overflow-x-auto no-scrollbar">
                        {experience.images.map((img, idx) => (
                            <div
                                key={idx}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedGalleryIndex(idx);
                                }}
                                className={`w-12 h-12 rounded-lg overflow-hidden cursor-pointer transition-all ${selectedGalleryIndex === idx ? 'ring-2 ring-white scale-110 opacity-100' : 'opacity-40 hover:opacity-70'
                                    }`}
                            >
                                <img src={img.url || img} className="w-full h-full object-cover" />
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Video Modal */}
            {showVideoModal && getCoverVideo() && (
                <div className="fixed inset-0 z-50 bg-black/95 backdrop-blur-xl flex items-center justify-center p-6" onClick={() => setShowVideoModal(false)}>
                    <div className="relative w-full max-w-5xl aspect-video rounded-3xl overflow-hidden shadow-2xl animate-in zoom-in duration-300" onClick={e => e.stopPropagation()}>
                        <video src={getCoverVideo()} controls autoPlay className="w-full h-full object-contain" />
                        <button
                            className="absolute top-6 right-6 w-12 h-12 bg-white/20 hover:bg-white/40 backdrop-blur-md rounded-full flex items-center justify-center transition-all border border-white/20"
                            onClick={() => setShowVideoModal(false)}
                        >
                            <FaTimes className="text-white" />
                        </button>
                    </div>
                </div>
            )}
        </div>
        </MainLayout>
    );
};

export default ExperienceDetailsPage;
